import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DollarSign, Users, AlertTriangle, ShoppingBag, TrendingUp, Receipt } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

function fmt(n) {
  return new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(n || 0);
}

function Kpi({ icon: Icon, label, value, sub, accent, testid }) {
  return (
    <div className="kpi-card animate-fade-up" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-wider text-muted-foreground font-semibold">{label}</div>
          <div className="font-heading text-3xl font-semibold text-navy mt-2">{value}</div>
          {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
        </div>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${accent || "bg-navy/5 text-navy"}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { isAdmin } = useAuth();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data)).catch(() => {});
  }, []);

  if (!data) {
    return <div className="text-muted-foreground" data-testid="dashboard-loading">Loading dashboard...</div>;
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-semibold text-navy">Dashboard</h1>
          <p className="text-sm text-muted-foreground">Live snapshot of today’s activity.</p>
        </div>
        <div className="flex gap-2">
          <Link to="/sales/new"><Button data-testid="dash-add-sale-btn" className="bg-gold hover:bg-gold-hover text-white">Add Sale</Button></Link>
          <Link to="/customers"><Button data-testid="dash-add-customer-btn" variant="outline">Customers</Button></Link>
          <Link to="/inventory"><Button data-testid="dash-add-stock-btn" variant="outline">Inventory</Button></Link>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi testid="kpi-today-sales" icon={DollarSign} label="Today's Sales" value={fmt(data.today_total)}
             sub={`${data.today_count} transactions`} accent="bg-gold/10 text-gold" />
        <Kpi testid="kpi-month-sales" icon={TrendingUp} label="This Month" value={fmt(data.month_total)}
             sub={isAdmin && data.month_profit != null ? `Profit ~ ${fmt(data.month_profit)}` : "Revenue"} />
        <Kpi testid="kpi-customers" icon={Users} label="Customers" value={data.total_customers}
             sub="Total in database" />
        <Kpi testid="kpi-low-stock" icon={AlertTriangle} label="Low Stock Alerts" value={data.low_stock_count}
             sub="Items needing reorder" accent="bg-red-50 text-red-600" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <Card className="lg:col-span-2 p-6 section-card" data-testid="recent-sales-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg font-semibold text-navy flex items-center gap-2">
              <Receipt className="w-4 h-4" /> Recent Sales
            </h3>
            <Link to="/sales" className="text-xs text-gold hover:underline" data-testid="recent-sales-view-all">View all →</Link>
          </div>
          {data.recent_sales.length === 0 ? (
            <div className="text-sm text-muted-foreground py-8 text-center">No sales yet today. Click “Add Sale” to start.</div>
          ) : (
            <div className="divide-y divide-border/60">
              {data.recent_sales.map((s) => (
                <div key={s.id} className="flex items-center justify-between py-3" data-testid={`recent-sale-${s.id}`}>
                  <div>
                    <div className="text-sm font-medium text-navy">{s.customer_name}</div>
                    <div className="text-xs text-muted-foreground">{new Date(s.created_at).toLocaleString()}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge variant="outline" className="text-xs">{s.payment_method}</Badge>
                    <div className="font-semibold text-navy">{fmt(s.total)}</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        <div className="space-y-6">
          <Card className="p-6 section-card" data-testid="top-products-card">
            <h3 className="font-heading text-lg font-semibold text-navy flex items-center gap-2 mb-4">
              <ShoppingBag className="w-4 h-4" /> Top Selling
            </h3>
            {data.top_products.length === 0 ? (
              <div className="text-sm text-muted-foreground">No sales this month yet.</div>
            ) : (
              <ol className="space-y-2.5">
                {data.top_products.map((p, i) => (
                  <li key={p.name} className="flex items-center justify-between text-sm">
                    <div className="flex items-center gap-3">
                      <span className="w-6 h-6 rounded bg-gold/10 text-gold text-xs font-semibold flex items-center justify-center">{i + 1}</span>
                      <span className="font-medium text-navy">{p.name}</span>
                    </div>
                    <span className="text-muted-foreground">{p.qty} units</span>
                  </li>
                ))}
              </ol>
            )}
          </Card>

          <Card className="p-6 section-card" data-testid="low-stock-card">
            <h3 className="font-heading text-lg font-semibold text-navy flex items-center gap-2 mb-4">
              <AlertTriangle className="w-4 h-4 text-red-500" /> Low / Out of Stock
            </h3>
            {data.low_stock.length === 0 ? (
              <div className="text-sm text-muted-foreground">All products are well stocked.</div>
            ) : (
              <ul className="space-y-2">
                {data.low_stock.map((p) => (
                  <li key={p.id} className="flex items-center justify-between text-sm">
                    <span className="text-navy font-medium truncate pr-2">{p.name}</span>
                    <Badge className={p.quantity === 0 ? "bg-destructive text-white" : "bg-gold text-white"}>
                      {p.quantity} left
                    </Badge>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
